import React, { useMemo, useContext } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, MessageSquare, Calendar, Award } from "lucide-react";
import { motion } from "framer-motion";
import nominationsService from "../services/nominationsService";
import usersService from "../services/usersService";
import AuthContext from "../context/AuthContext";
import UserAvatar from "../components/UserAvatar";

const CampaignNominationsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: currentUser } = useContext(AuthContext);

  const awardId = searchParams.get("award_id");
  const recipientId = searchParams.get("recipient_id");

  const { data: campaign, isLoading: campaignLoading } = useQuery({
    queryKey: ["admin-campaign", id],
    queryFn: () => nominationsService.getCampaignById(id),
    enabled: !!id,
  });

  const { data: awardsResponse } = useQuery({
    queryKey: ["campaign-awards", id],
    queryFn: () => nominationsService.getCampaignAwards(id, true),
    enabled: !!id,
  });

  const { data: usersData } = useQuery({
    queryKey: ["colleagues"],
    queryFn: () => usersService.getColleagues(0, 500),
  });

  const awards = useMemo(() => awardsResponse?.data || [], [awardsResponse]);

  const usersMap = useMemo(() => {
    const map = {};
    if (currentUser) map[currentUser.id] = currentUser;
    const list = Array.isArray(usersData) ? usersData : usersData?.data || [];
    list.forEach((u) => { map[u.id] = u; });
    return map;
  }, [usersData, currentUser]);

  const award = useMemo(() => awards.find((a) => a.id === awardId), [awards, awardId]);
  const recipient = usersMap[recipientId];

  const filteredNominations = useMemo(() => {
    if (!campaign?.nominations) return [];
    return campaign.nominations.filter((n) => {
      if (awardId && n.award_id !== awardId) return false;
      if (recipientId && n.recipient_id !== recipientId) return false;
      return true;
    }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [campaign?.nominations, awardId, recipientId]);

  if (campaignLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-4xl mx-auto px-4 pb-8 pt-3 sm:pt-8 text-center">
        <p className="text-slate-600">Campaign not found.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 pb-8 pt-3 sm:pt-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/admin/campaigns/${id}?tab=leaderboard`)}
          className="p-2 rounded-full hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Nominations for {recipient?.full_name || recipient?.email || "User"}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-sm text-slate-500">{campaign.name}</span>
            {award && (
              <>
                <span className="text-slate-300">·</span>
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  <span>{award.emoji && award.emoji.startsWith('/api/v1/recognition-type-icons/') ? <img src={award.emoji} alt="" className="w-4 h-4 object-contain inline" /> : (award.emoji || "⭐")}</span> {award.name}
                </span>
              </>
            )}
            <span className="text-slate-300">·</span>
            <span className="text-sm font-medium text-purple-600">
              {filteredNominations.length} nomination{filteredNominations.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>

      {/* Nominations list */}
      {filteredNominations.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-md p-12 text-center">
          <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-600">No nominations found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredNominations.map((nom, idx) => {
            const sender = usersMap[nom.sender_id];
            const nomAward = awards.find((a) => a.id === nom.award_id);

            return (
              <motion.div
                key={nom.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white rounded-2xl shadow-md p-6"
              >
                <div className="flex items-start gap-4">
                  {/* Sender avatar */}
                  <div className="flex-shrink-0">
                    <UserAvatar src={sender?.avatar_url} name={sender?.full_name || sender?.email} className="w-10 h-10 rounded-full object-cover" textClassName="text-sm" />
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Sender name and timestamp */}
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-slate-900">
                        {sender?.full_name || sender?.email || "Unknown User"}
                      </p>
                      {nom.timestamp && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(nom.timestamp).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                      )}
                    </div>

                    {/* Award badge */}
                    {nomAward && (
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-medium mb-3">
                        <Award className="w-3 h-3" />
                        <span>{nomAward.emoji || "⭐"}</span> {nomAward.name}
                      </div>
                    )}

                    {/* Message */}
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{nom.message}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CampaignNominationsPage;
